import { Link, Navigate, useParams } from 'react-router-dom';
import { GraduationCap, Package } from 'lucide-react';
import { ErrorState, Skeleton, SubBar } from '@/design/components';
import { formatDate } from '@/lib/dates';
import { useImageUrl, useProduct, useProductQuiz } from './api';

/** דף מוצר אחד: מה הוא, ומה חשוב להגיד עליו ללקוח (PRD 5.7.1). */
export function ProductPage() {
  const { productId = '' } = useParams();
  const product = useProduct(productId);
  const quizId = useProductQuiz(productId);
  const image = useImageUrl(product.data?.image_path);

  if (product.isPending) return <Skeleton className="m-4 h-64" />;
  if (product.isError) return <ErrorState onRetry={() => void product.refetch()} />;
  if (!product.data) return <Navigate to="/products" replace />;

  const item = product.data;

  return (
    <div className="grid content-start gap-4">
      {/* כל המחלקות ולא רק הראשונה: מוצר יכול לשבת בכמה */}
      <SubBar
        title={item.name}
        subtitle={
          item.product_category_links
            .map((link) => link.product_categories?.name)
            .filter(Boolean)
            .join(' · ') || undefined
        }
      />

      <div className="grid gap-4 px-4 pb-8">
        <div className="grid aspect-video place-items-center overflow-hidden rounded-md bg-olive-soft">
          {image.data ? (
            <img src={image.data} alt={item.name} className="size-full object-cover" />
          ) : (
            <Package size={48} strokeWidth={1.25} aria-hidden="true" className="text-muted" />
          )}
        </div>

        {(item.name_en || item.sku) && (
          <p className="m-0 text-body-sm text-muted">
            {item.name_en}
            {item.name_en && item.sku && ' · '}
            {item.sku && <span className="tabular">מק״ט {item.sku}</span>}
          </p>
        )}

        {item.description && (
          <section className="grid gap-1">
            <h2 className="m-0 font-display text-display-m font-semibold">על המוצר</h2>
            <p className="m-0 whitespace-pre-line text-body leading-relaxed">{item.description}</p>
          </section>
        )}

        {item.selling_points && (
          <section className="grid gap-1 rounded-md border border-line bg-raised p-3">
            <h2 className="m-0 font-display text-display-m font-semibold">מה להגיד ללקוח</h2>
            <p className="m-0 whitespace-pre-line text-body leading-relaxed">
              {item.selling_points}
            </p>
          </section>
        )}

        {quizId.data && (
          <Link
            to={`/learning/quiz/${quizId.data}`}
            className="inline-flex w-fit items-center gap-2 rounded-pill bg-charcoal px-4 py-2 text-body-sm font-medium text-on-charcoal no-underline"
          >
            <GraduationCap size={20} strokeWidth={1.75} aria-hidden="true" />
            לחידון על המוצר
          </Link>
        )}

        {item.launched_at && (
          <p className="m-0 text-label text-muted">נכנס למכירה ב {formatDate(item.launched_at)}</p>
        )}
      </div>
    </div>
  );
}
